import { type Component, type JSX, createContext, useContext, createSignal, For } from 'solid-js'
import { Portal } from 'solid-js/web'
import NotificationCard from './NotificationCard'
import type {
  NotificationItem,
  NotificationSeverity,
  NotificationOptions,
  ProgressOptions,
  NotificationAPI
} from './types'

let nextId = 0
const genId = (): string => `notif-${++nextId}-${Date.now()}`

const DEFAULT_TTL = 5000

const NotificationContext = createContext<NotificationAPI>()

export const NotificationProvider: Component<{ children: JSX.Element }> = (props) => {
  const [items, setItems] = createSignal<NotificationItem[]>([])

  const push = (severity: NotificationSeverity, message: JSX.Element, options?: NotificationOptions): string => {
    const id = genId()
    const sticky = options?.sticky ?? false
    const ttl = sticky ? 0 : options?.ttl ?? DEFAULT_TTL
    setItems((prev) => [
      ...prev,
      { id, severity, message, sticky, ttl, createdAt: Date.now(), onClick: options?.onClick }
    ])
    return id
  }

  const dismiss = (id: string): void => {
    setItems((prev) => prev.filter((item) => item.id !== id))
  }

  const dismissAll = (): void => {
    setItems([])
  }

  const update = (
    id: string,
    severity: NotificationSeverity,
    message: JSX.Element,
    options?: NotificationOptions
  ): void => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              severity,
              message,
              sticky: options?.sticky ?? item.sticky,
              ttl: options?.sticky ? 0 : options?.ttl ?? DEFAULT_TTL,
              createdAt: Date.now(),
              onClick: options?.onClick ?? item.onClick
            }
          : item
      )
    )
  }

  const api: NotificationAPI = {
    notify: (message, options) => push('notify', message, options),
    info: (message, options) => push('info', message, options),
    warn: (message, options) => push('warn', message, options),
    error: (message, options) => push('error', message, options),
    success: (message, options) => push('success', message, options),

    progress: <T,>(message: JSX.Element, options: ProgressOptions<T>): string => {
      const id = push('progress', message, { sticky: true, ...options })

      options.task().then(
        (value) => {
          const successMsg = options.onSuccess ? options.onSuccess(value) : 'Done'
          update(id, 'success', successMsg, {
            ttl: options.ttl ?? DEFAULT_TTL,
            sticky: false,
            onClick: options.onClick
          })
        },
        (err) => {
          const errorMsg = options.onError ? options.onError(err) : 'Something went wrong'
          update(id, 'error', errorMsg, { ttl: options.ttl ?? 8000, sticky: false, onClick: options.onClick })
        }
      )

      return id
    },

    update,
    dismiss,
    dismissAll
  }

  return (
    <NotificationContext.Provider value={api}>
      {props.children}
      <Portal>
        <div class="fixed top-20 right-6 z-[9999] pointer-events-none">
          <For each={items()}>
            {(item, index) => {
              const depth = () => items().length - 1 - index()
              const capped = () => Math.min(depth(), 3)
              return (
                <div
                  class="pointer-events-auto absolute right-0 top-0 transition-all duration-300 ease-out"
                  classList={{ relative: index() === items().length - 1 }}
                  style={{
                    transform: `translateY(${capped() * 8}px) scale(${1 - capped() * 0.04})`,
                    'transform-origin': 'bottom center',
                    opacity: capped() > 2 ? '0' : '1',
                    'z-index': `${index()}`
                  }}
                >
                  <NotificationCard item={item} onDismiss={dismiss} />
                </div>
              )
            }}
          </For>
        </div>
      </Portal>
    </NotificationContext.Provider>
  )
}

export const useNotification = (): NotificationAPI => {
  const ctx = useContext(NotificationContext)
  if (!ctx) throw new Error('useNotification must be used inside a NotificationProvider')
  return ctx
}
